import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail, formatMoney, PAYMENT_METHODS } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

export default function PlanItemPaymentDialog({ open, onOpenChange, item, onDone }) {
  const [method, setMethod] = useState("efectivo");
  const [amount, setAmount] = useState("");
  const [cashOpen, setCashOpen] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMethod("efectivo");
    setAmount(String(item?.price || ""));
    setCashOpen(null);
    api.get("/cashbox/today")
      .then((r) => setCashOpen(!!(r.data.session && r.data.session.status === "abierta")))
      .catch(() => setCashOpen(false));
  }, [open, item]);

  const submit = async (registerPayment) => {
    setLoading(true);
    try {
      await api.post(`/plan-items/${item.id}/complete`, { payment_method: method, amount: Number(amount) || 0, register_payment: registerPayment });
      toast.success(registerPayment ? `Cobro de ${formatMoney(Number(amount) || 0)} registrado en caja` : "Procedimiento marcado como realizado");
      onOpenChange(false);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="plan-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-primary" /> Registrar procedimiento realizado</DialogTitle>
          <DialogDescription>
            <b>{item?.name}</b>{item?.tooth ? ` · Diente ${item.tooth}` : ""}. Indica cómo se recibió el pago para sumarlo a la caja del día.
          </DialogDescription>
        </DialogHeader>

        {cashOpen === false && (
          <div data-testid="plan-cash-closed-warning" className="flex items-start gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span><b>La caja está cerrada.</b> Para registrar este cobro debes abrir la caja del día en <b>Caja → Abrir caja</b>.</span>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="label-eyebrow block mb-2">Medio de pago</label>
            <div className="grid grid-cols-3 gap-2">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.key} type="button" onClick={() => setMethod(m.key)} data-testid={`plan-pay-method-${m.key}`}
                  className={`h-9 rounded-sm text-[10px] uppercase tracking-widest font-bold border transition-colors ${method === m.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                >
                  {m.short}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Monto recibido</label>
            <input type="number" min="0" step="1000" className="input-flat font-mono" data-testid="plan-pay-amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-end pt-1">
            <button type="button" onClick={() => submit(false)} disabled={loading} data-testid="plan-pay-skip" className="h-10 px-4 rounded-sm border border-border text-xs hover:bg-muted disabled:opacity-60">
              Marcar realizado sin cobro
            </button>
            <button type="button" onClick={() => submit(true)} disabled={loading || cashOpen === false || !(Number(amount) > 0)} data-testid="plan-pay-confirm" className="btn-primary justify-center disabled:opacity-60">
              {loading ? "Registrando…" : `Cobrar ${formatMoney(Number(amount) || 0)}`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
